import { useEffect, useState } from 'react';

import {
  useAddMyPersonalContact,
  useMyPersonalContacts,
  useRemoveMyPersonalContact,
} from '../../hooks/useMyPrivacy';
import type { PersonalContact } from '../../types';
import styles from '../admin/crmAdmin.module.css';

const PAGE_SIZE = 25;

function initials(name: string | null, email: string): string {
  const base = (name || email).trim();
  const parts = base.split(/[\s@.]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Debounce a fast-changing value so we don't fire a query per keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Settings → My CRM Privacy. A per-user, self-service view of the firm
 * personal-contacts list: a caller sees + manages only the rows THEY added
 * (scoped by the `added_by` attribution stamp), but the suppression itself is
 * FIRM-WIDE and retroactive — anyone on the list never enters the CRM, for any
 * user, at ingest. A copy of CRM Admin → Privacy trimmed to the self-service
 * axes (no cross-user segment / added-by filters).
 */
export function MyPrivacySection() {
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const search = useDebounced(searchInput, 300);

  // Reset to page 1 whenever the filter changes — adjust state during render
  // (the "store info from previous renders" pattern) rather than in an effect.
  const [prevSearch, setPrevSearch] = useState(search);
  if (search !== prevSearch) {
    setPrevSearch(search);
    setPage(1);
  }

  const query = useMyPersonalContacts({ search, page, pageSize: PAGE_SIZE });
  const addContact = useAddMyPersonalContact();
  const removeContact = useRemoveMyPersonalContact();

  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [removeTarget, setRemoveTarget] = useState<PersonalContact | null>(null);
  const [statusLine, setStatusLine] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null);

  const data = query.data;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Never strand the user past the end: removing the last row of page > 1 shrinks
  // totalPages while `page` stays put, and the pager (rendered only when
  // totalPages > 1) would vanish, leaving an empty page with no way back. Clamp
  // during render — it converges (page === totalPages ends the condition) and
  // re-queries a valid page (review finding #3). Gate on `data`: on a forward
  // page change the new queryKey has no cache so `data` is briefly undefined
  // (total→0, totalPages→1) — clamping then would bounce the user back and break
  // "Next". Only clamp once the current page's data has actually settled.
  if (data && page > totalPages) setPage(totalPages);
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const submitAdd = () => {
    const email = addEmail.trim();
    if (!email || !email.includes('@')) return;
    addContact.mutate(
      { email },
      {
        onSuccess: (res) => {
          setAddOpen(false);
          setAddEmail('');
          if (res.already_suppressed && !res.owned) {
            // Takeover guard: the address is already hidden firm-wide by someone
            // else. It stays hidden; this caller does not own it and it will NOT
            // appear in their list. Be explicit so the empty result isn't confusing.
            setStatusLine({
              text: `${res.email} is already hidden firm-wide (added by someone else). It stays hidden — it won't appear in your list to manage.`,
              tone: 'warn',
            });
          } else if (res.already_suppressed) {
            setStatusLine({ text: `${res.email} is already on your list.`, tone: 'ok' });
          } else {
            setStatusLine({
              text: `Added ${res.email} — their mail and meetings are now hidden from the CRM.`,
              tone: 'ok',
            });
          }
        },
      },
    );
  };

  const submitRemove = () => {
    if (!removeTarget) return;
    removeContact.mutate(removeTarget, {
      onSuccess: (res) => {
        setRemoveTarget(null);
        setStatusLine({
          text: `Removed ${res.email} — their mail and meetings will re-surface into the CRM.`,
          tone: 'ok',
        });
      },
    });
  };

  return (
    <section id="settings-privacy" className={styles.card}>
      <div className={styles.cardHeader}>
        <h2 className={styles.title}>My CRM Privacy</h2>
        <p className={styles.sub}>
          Add personal contacts (spouse, doctor, family, sensitive relationships) whose mail and
          meetings should <b>never enter the CRM</b>. Anything you add here is hidden{' '}
          <b>firm-wide</b> — for every user, including admins — at ingest, and it's retroactive:
          existing mail and meetings involving that address are re-hidden too. You see and manage
          only the contacts <b>you</b> added; removing one re-surfaces them.
        </p>
      </div>

      <div className={styles.body}>
        <div className={styles.toolbar}>
          <label className={styles.search}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
            </svg>
            <input
              placeholder="Search email…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </label>
          <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setAddOpen(true)}>
            + Add contact
          </button>
        </div>

        {statusLine && (
          <div
            className={styles.count}
            style={{ color: statusLine.tone === 'warn' ? '#B7791F' : '#2E7D32' }}
          >
            {statusLine.text}
          </div>
        )}

        {query.isError && (
          <div className={styles.error}>
            Failed to load your privacy list: {(query.error as Error)?.message ?? 'unknown error'}
          </div>
        )}
        {query.isLoading && <div className={styles.loading}>Loading…</div>}

        {!query.isLoading && !query.isError && (
          <>
            <div className={styles.count}>
              Showing {rangeStart}–{rangeEnd} of {total}
            </div>

            {rows.length === 0 ? (
              <div className={styles.empty}>
                {search ? (
                  <>
                    <h3>No matches</h3>
                    <p>None of your personal contacts match “{search}”.</p>
                  </>
                ) : (
                  <>
                    <h3>You haven't added anyone</h3>
                    <p>
                      Add a personal contact to hide their mail and meetings from the CRM, firm-wide.
                      Anyone not on the list is visible by default.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Added on</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <div className={styles.who}>
                            <div className={styles.ava}>{initials(c.contact_name, c.email)}</div>
                            <div>
                              {c.contact_name && <div className={styles.nm}>{c.contact_name}</div>}
                              <div className={styles.em}>{c.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className={styles.on}>{formatDate(c.added_at)}</td>
                        <td className={styles.actCol}>
                          <button
                            className={styles.kebab}
                            title="Remove from list"
                            onClick={() => setRemoveTarget(c)}
                          >
                            ⋯
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className={styles.pager}>
                <button
                  className={styles.pagerBtn}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ← Prev
                </button>
                <span className={styles.pagerInfo}>Page {page} of {totalPages}</span>
                <button
                  className={styles.pagerBtn}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}

        <div className={styles.callout}>
          <span className={styles.calloutIcon}>●</span>
          <div>
            <b>Adding a contact is firm-wide and retroactive.</b> It hides every past and future
            message and meeting involving that address, for every user — it never enters the CRM.
            It's reversible: removing it re-surfaces them. If an address is already hidden by someone
            else, it stays hidden and can't be managed from here.
          </div>
        </div>
      </div>

      {addOpen && (
        <div className={styles.modalOverlay} onClick={() => setAddOpen(false)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>Add a personal contact</div>
            <div className={styles.modalBody}>
              <p>
                Hides every past and future message and meeting involving this address, firm-wide,
                for every user. Reversible.
              </p>
              <input
                className={styles.modalInput}
                type="email"
                placeholder="name@example.com"
                value={addEmail}
                autoFocus
                onChange={(e) => setAddEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); }}
              />
              {addContact.isError && (
                <div className={styles.error}>
                  {(addContact.error as Error)?.message ?? 'Add failed'}
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={`${styles.btn} ${styles.btnGhost}`}
                onClick={() => { setAddOpen(false); setAddEmail(''); addContact.reset(); }}
              >
                Cancel
              </button>
              <button
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={addContact.isPending || !addEmail.includes('@')}
                onClick={submitAdd}
              >
                {addContact.isPending ? 'Adding…' : 'Add to list'}
              </button>
            </div>
          </div>
        </div>
      )}

      {removeTarget && (
        <div className={styles.modalOverlay} onClick={() => setRemoveTarget(null)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>Remove from your privacy list?</div>
            <div className={styles.modalBody}>
              <p>
                <b>{removeTarget.contact_name || removeTarget.email}</b> will be removed. Their mail
                and meetings re-surface into the CRM. You can add them back at any time.
              </p>
              {removeContact.isError && (
                <div className={styles.error}>
                  {(removeContact.error as Error)?.message ?? 'Remove failed'}
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={`${styles.btn} ${styles.btnGhost}`}
                onClick={() => { setRemoveTarget(null); removeContact.reset(); }}
              >
                Cancel
              </button>
              <button
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={removeContact.isPending}
                onClick={submitRemove}
              >
                {removeContact.isPending ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
